import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { useMapContext } from 'widgets/earthMap/contexts/mapContext';
import { Colors } from '@blueprintjs/core';
import IColorInfo from 'widgets/earthMap/interfaces/IColorInfo';
import numeral from 'numeral';

const tickFormatter = (value: number) => {
	if (isNaN(value)) return 'NaN';
	else if (value === Infinity) return 'Inf';
	return numeral(value).format('0.[00]a');
};

export default function EarthMapLegend() {
	const [{ colorInfo, clusterColorInfo, isClustered, selectedFeatureProperty }] = useMapContext();

	const sortedColorInfo = useMemo(() => {
		const a = isClustered ? clusterColorInfo : colorInfo;
		return [...a].sort((a: IColorInfo, b: IColorInfo) => {
			if (a.maxValue < b.maxValue) return 1;
			else if (a.maxValue > b.maxValue) return -1;
			else return 0;
		});
	}, [colorInfo, clusterColorInfo, isClustered]);

	if (!isClustered && !selectedFeatureProperty) return null;
	return (
		<Wrapper>
			<InnerWrapper>
				<VerticalSpace space={'6px'} />
				{sortedColorInfo.slice(0, 6).map((info: IColorInfo, index: number) => (
					<React.Fragment key={index}>
						<Tick />
						<ColorBlock blockColor={info.color} />
					</React.Fragment>
				))}
				<Tick />
			</InnerWrapper>
			<HorizontalSpace space={'4px'} />
			<InnerWrapper>
				{sortedColorInfo.map((info: IColorInfo, index: number) => (
					<React.Fragment key={index}>
						<TickLabel>{tickFormatter(info.maxValue)}</TickLabel>
						<VerticalSpace space={'8px'} />
					</React.Fragment>
				))}
				<TickLabel>{tickFormatter(sortedColorInfo?.[sortedColorInfo?.length - 1]?.minValue)}</TickLabel>
			</InnerWrapper>
		</Wrapper>
	);
}
const TickLabel = styled.div`
	height: 13px;
	display: flex;
	align-items: center;
	font-size: 8px;
`;

const InnerWrapper = styled.div`
	position: relative;
`;

interface ISpace {
	space: string;
}
const VerticalSpace = styled.div<ISpace>`
	padding-top: ${(props: ISpace) => props.space};
`;
const HorizontalSpace = styled.div<ISpace>`
	padding-left: ${(props: ISpace) => props.space};
`;

const Wrapper = styled.div`
	position: absolute;
	right: 12px;
	bottom: 12px;
	padding: 12px;
	background: ${Colors.WHITE};
	display: flex;
`;

const Tick = styled.div`
	width: 15px;
	height: 1px;
	background: ${Colors.BLACK};
`;

interface IColorBlock {
	blockColor: string;
}
const ColorBlock = styled.div`
	background: ${(props: IColorBlock) => props.blockColor};
	height: 20px;
	width: 10px;
`;
